/**
 * Turning the time-zone editors on screen into the two maps the API stores.
 *
 * ★ PURE AND IMPORT-FREE ON PURPOSE. The screen that uses these cannot be
 * imported by a CRA test — it pulls in RTK Query, whose ESM dependency the
 * jest transform refuses — so anything worth asserting lives here instead.
 *
 * ★ `normaliseCity` MIRRORS THE SERVER'S. The server normalises city keys on
 * save, so a row typed as "St. Petersburg" is stored as "st petersburg". This
 * copy exists only so the screen can tell, before saving, whether a city is
 * already recognised. The server remains the authority — see
 * `back/services/meta-whatsapp/campaign/cityZones.js`.
 */

export const normaliseCity = (value) =>
	String(value ?? '')
		.normalize('NFKD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[.,'’`-]/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

const isRegion = (value) => /^[A-Z]{2}$/.test(value);

/** { US: 'America/New_York' } → [{ region, zone }], stable order. */
export const tzRowsFromMap = (map = {}) =>
	Object.entries(map ?? {})
		.map(([region, zone]) => ({ region: String(region).toUpperCase(), zone: String(zone ?? '') }))
		.sort((a, b) => a.region.localeCompare(b.region));

/** The inverse. Rows without both halves are dropped rather than sent empty. */
export const tzMapFromRows = (rows = []) => {
	const out = {};
	for (const row of rows ?? []) {
		const region = String(row?.region ?? '').trim().toUpperCase();
		const zone = String(row?.zone ?? '').trim();
		if (!isRegion(region) || !zone) continue;
		out[region] = zone;
	}
	return out;
};

/** { US: { 'new york': 'America/New_York' } } → [{ region, city, zone }]. */
export const cityRowsFromMap = (map = {}) => {
	const rows = [];
	for (const [region, cities] of Object.entries(map ?? {})) {
		for (const [city, zone] of Object.entries(cities ?? {})) {
			rows.push({ region: String(region).toUpperCase(), city, zone: String(zone ?? '') });
		}
	}
	return rows.sort((a, b) => a.region.localeCompare(b.region) || a.city.localeCompare(b.city));
};

/**
 * The inverse, nested back the way the API stores it.
 *
 * A half-finished row — a country chosen but no city typed yet — is dropped
 * rather than sent, because the server would refuse the whole save and the
 * person would lose every other edit on the screen along with it.
 */
export const cityMapFromRows = (rows = []) => {
	const out = {};
	for (const row of rows ?? []) {
		const region = String(row?.region ?? '').trim().toUpperCase();
		const city = normaliseCity(row?.city);
		const zone = String(row?.zone ?? '').trim();
		if (!isRegion(region) || !city || !zone) continue;
		if (!out[region]) out[region] = {};
		out[region][city] = zone;
	}
	return out;
};

/**
 * What to say under a city row, given the cities the engine already knows.
 *
 * ★ WITHOUT THIS THE EDITOR IS BLIND. About 150 cities are recognised already;
 * someone adding "Seattle" has no way to tell whether they are fixing something
 * or restating it, and no way to notice they are quietly overriding a correct
 * built-in with a different zone.
 *
 * @returns {{tone: 'muted'|'warning', text: string}|null}
 */
export const catalogueNote = (row, catalogue = {}) => {
	const region = String(row?.region ?? '').trim().toUpperCase();
	const city = normaliseCity(row?.city);
	if (!isRegion(region) || !city) return null;

	const shipped = catalogue?.[region]?.[city];
	if (!shipped) return null;

	const zone = String(row?.zone ?? '').trim();
	if (!zone || zone === shipped) {
		return { tone: 'muted', text: 'Already recognised — this row changes nothing.' };
	}
	return { tone: 'warning', text: `Overrides the built-in ${shipped}.` };
};

/**
 * Indexes of rows repeating a country and city an earlier row already claimed.
 *
 * They collapse into one key on save, and because the map is built in order the
 * LAST one silently wins. So the later row is the one flagged: it is the one
 * that takes effect, and without the flag the earlier row's value would vanish
 * with nothing on screen to explain it.
 */
export const duplicateIndexes = (rows = []) => {
	const seen = new Set();
	const duplicates = new Set();
	(rows ?? []).forEach((row, index) => {
		const region = String(row?.region ?? '').trim().toUpperCase();
		const city = normaliseCity(row?.city);
		if (!isRegion(region) || !city) return;
		const key = `${region}/${city}`;
		if (seen.has(key)) duplicates.add(index);
		else seen.add(key);
	});
	return duplicates;
};
